import type { ReactNode } from 'react';

interface PageHeroProps {
  eyebrow?: string;
  eyebrowOrange?: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode; // CTA buttons
}

/**
 * Shared light-theme hero matching the FDME design system:
 * grid background, radial teal/orange glow, eyebrow pill,
 * Instrument Serif italic headline, Barlow lede.
 */
export default function PageHero({
  eyebrow,
  eyebrowOrange,
  title,
  subtitle,
  children,
}: PageHeroProps) {
  return (
    <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-20">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.14), transparent 58%), radial-gradient(ellipse at 85% 20%, rgba(249,115,22,0.10), transparent 55%)',
        }}
      ></div>
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center flex flex-col items-center">
        {eyebrow && (
          <div
            className="eyebrow"
            style={eyebrowOrange ? { color: '#EA580C', borderColor: 'rgba(249,115,22,0.3)' } : undefined}
          >
            {eyebrow}
          </div>
        )}
        <h1 className="mt-6 h-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em] max-w-4xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 font-body font-light leading-relaxed">
            {subtitle}
          </p>
        )}
        {children && <div className="mt-9 flex flex-col sm:flex-row items-center gap-4">{children}</div>}
      </div>
    </section>
  );
}
